<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1200px] mx-auto">

    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div class="flex items-center gap-4 min-w-0">
        <a [routerLink]="['/delivery-terms']" [queryParams]="editing ? { selected: editing.id } : {}"
          class="h-10 w-10 flex-shrink-0 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-amber-300 transition-colors"
          matTooltip="Back to delivery terms" aria-label="Back to delivery terms">
          <mat-icon>arrow_back</mat-icon>
        </a>
        <div class="min-w-0">
          <div class="flex items-center gap-1.5 text-xs text-gray-400">
            <a [routerLink]="['/delivery-terms']" class="hover:text-gray-700">Delivery Terms</a>
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">chevron_right</mat-icon>
            <span>{{ editing ? 'Edit' : 'New' }}</span>
          </div>
          <h1 class="text-2xl font-bold text-gray-900 tracking-tight truncate">
            {{ loading ? 'Loading…' : editing ? editing.deliveryTermDesc : 'New delivery term' }}
          </h1>
        </div>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
      @if (editing) {
        <app-record-nav noun="delivery term" listName="Delivery Terms" [position]="position" [total]="ordered.length"
          [prevName]="prev?.deliveryTermDesc ?? null" [nextName]="next?.deliveryTermDesc ?? null" [disabled]="saving" (prev)="goTo(prev)" (next)="goTo(next)"></app-record-nav>
      }
      @if (editing && canDelete) {
        <span [matTooltip]="editing.variants ? 'Used by ' + editing.variants + ' variant' + (editing.variants === 1 ? '' : 's') + ' - move them to another term first' : ''">
          <button type="button" class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:border-red-200 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40 disabled:pointer-events-none"
            [disabled]="editing.variants > 0" (click)="remove()">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">delete</mat-icon> Delete
          </button>
        </span>
      }
      </div>
    </div>

    @if (loading) {
      <div class="rounded-2xl bg-white border border-gray-100 py-24 flex flex-col items-center justify-center text-sm text-gray-500">
        <mat-spinner diameter="30"></mat-spinner><span class="mt-3">Loading&hellip;</span>
      </div>
    } @else if (notFound) {
      <div class="rounded-3xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
        <span class="mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[28px] !h-7 !w-7">search_off</mat-icon></span>
        <p class="mt-3 text-sm font-semibold text-gray-700">That delivery term doesn't exist</p>
        <a [routerLink]="['/delivery-terms']" class="mt-4 inline-block text-xs font-semibold text-amber-700 hover:underline">Back to delivery terms</a>
      </div>
    } @else {

    @if (editing && editing.variants) {
      <div class="mb-5 flex items-start gap-3 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900">
        <mat-icon class="!text-[20px] !h-5 !w-5 flex-shrink-0">info</mat-icon>
        <p class="text-sm">Used by <strong>{{ editing.variants | number }} variant{{ editing.variants === 1 ? '' : 's' }}</strong> on {{ editing.products | number }} product{{ editing.products === 1 ? '' : 's' }} - your changes show on all of them straight away.</p>
      </div>
    }

    <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start">

      <!-- ============ FORM ============ -->
      <div class="flex flex-col gap-5">

        <!-- Type -->
        <section class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="flex items-center gap-3 mb-4">
            <span class="h-9 w-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center"><mat-icon class="!text-[19px] !h-[19px] !w-[19px]">category</mat-icon></span>
            <div><h2 class="text-[15px] font-bold text-gray-900">Type</h2><p class="text-xs text-gray-500">How checkout treats items with this term</p></div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            @for (k of kinds; track k.key) {
            <button type="button" class="relative flex sm:flex-col items-center sm:items-start gap-3 sm:gap-2 p-4 rounded-2xl border-2 text-left transition-all"
              [ngClass]="kind === k.key ? 'border-amber-400 bg-amber-50/70' : 'border-gray-100 bg-gray-50/50 hover:border-gray-200 hover:bg-white'" (click)="setKind(k.key)">
              @if (kind === k.key) { <mat-icon class="absolute top-3 right-3 !text-[18px] !h-[18px] !w-[18px] text-amber-500">check_circle</mat-icon> }
              <span class="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0" [ngClass]="kind === k.key ? 'bg-amber-400 text-neutral-900' : 'bg-white text-gray-500 border border-gray-200'"><mat-icon class="!text-[20px] !h-5 !w-5">{{ k.icon }}</mat-icon></span>
              <span>
                <span class="block text-sm font-bold" [ngClass]="kind === k.key ? 'text-amber-900' : 'text-gray-800'">{{ k.label }}</span>
                <span class="block text-xs text-gray-500">{{ k.hint }}</span>
              </span>
            </button>
            }
          </div>
        </section>

        <!-- Details -->
        <section class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="flex items-center gap-3 mb-5">
            <span class="h-9 w-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center"><mat-icon class="!text-[19px] !h-[19px] !w-[19px]">edit_note</mat-icon></span>
            <div><h2 class="text-[15px] font-bold text-gray-900">Details</h2><p class="text-xs text-gray-500">What customers see on the product page</p></div>
          </div>

          @switch (kind) {
            @case ('RANGE') {
              <div class="mb-5">
                <div class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Working days</div>
                <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-3 max-w-md">
                  @for (side of ['from', 'to']; track side) {
                    @if (side === 'to') { <span class="text-gray-300 font-bold text-xl">–</span> }
                    <div>
                      <div class="flex items-center rounded-2xl border border-gray-200 bg-gray-50 focus-within:bg-white focus-within:border-amber-400 focus-within:ring-4 focus-within:ring-amber-100 transition-all">
                        <button type="button" class="h-12 w-11 text-gray-400 hover:text-gray-900 flex items-center justify-center" (click)="step($any(side), -1)" [attr.aria-label]="'Fewer days (' + side + ')'"><mat-icon>remove</mat-icon></button>
                        @if (side === 'from') {
                          <input type="number" min="1" step="1" [(ngModel)]="from" (ngModelChange)="syncDescription()" class="w-full min-w-0 bg-transparent text-center text-xl font-bold tabular-nums outline-none" aria-label="From (working days)" />
                        } @else {
                          <input type="number" min="1" step="1" [(ngModel)]="to" (ngModelChange)="syncDescription()" class="w-full min-w-0 bg-transparent text-center text-xl font-bold tabular-nums outline-none" aria-label="To (working days)" />
                        }
                        <button type="button" class="h-12 w-11 text-gray-400 hover:text-gray-900 flex items-center justify-center" (click)="step($any(side), 1)" [attr.aria-label]="'More days (' + side + ')'"><mat-icon>add</mat-icon></button>
                      </div>
                      <div class="mt-1 text-center text-[11px] text-gray-400 capitalize">{{ side }}</div>
                    </div>
                  }
                </div>

                <!-- Where the range sits next to the others -->
                @if (from && to && from <= to) {
                  <div class="mt-4 max-w-md">
                    <div class="relative h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div class="absolute inset-y-0 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 transition-all duration-300"
                        [style.left.%]="((from - 1) / maxDays) * 100" [style.width.%]="Math.max(((to - from + 1) / maxDays) * 100, 3)"></div>
                    </div>
                    <div class="mt-1 flex justify-between text-[10px] text-gray-400 tabular-nums"><span>1 day</span><span>{{ maxDays }} days</span></div>
                  </div>
                }
              </div>
            }
            @case ('CALL') {
              <div class="mb-5 flex items-start gap-3 px-4 py-3 rounded-2xl bg-sky-50 text-sky-800">
                <mat-icon class="!text-[20px] !h-5 !w-5 flex-shrink-0">info</mat-icon>
                <p class="text-sm leading-relaxed">If any item in the basket has this term, checkout asks the customer to call to arrange delivery instead of showing a date.</p>
              </div>
            }
            @default {
              <div class="mb-5 max-w-md">
                <label class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Slug</label>
                <input type="text" [(ngModel)]="otherSlug" placeholder="e.g. collection" class="mt-1 w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm font-mono outline-none focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100" />
                <p class="mt-1.5 flex items-start gap-1 text-xs text-amber-700"><mat-icon class="!text-[14px] !h-3.5 !w-3.5 flex-shrink-0 mt-px">warning_amber</mat-icon>Checkout only reads day ranges like 3-5, or "call" - with this it shows no delivery estimate.</p>
              </div>
            }
          }

          <div>
            <div class="flex items-baseline justify-between gap-2">
              <label class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Description</label>
              @if (suggestedDescription && description !== suggestedDescription) {
                <button type="button" class="text-xs font-semibold text-amber-700 hover:underline" (click)="useSuggested()">Use "{{ suggestedDescription }}"</button>
              }
            </div>
            <input type="text" [ngModel]="description" (ngModelChange)="onDescriptionTyped($event)" placeholder="Shown to customers on the product page"
              class="mt-1 w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-[15px] outline-none focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100" />
            <div class="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
              <span>Slug</span>
              <span class="font-mono px-2 py-0.5 rounded-md bg-gray-100 text-gray-800">{{ slug || '—' }}</span>
              <span class="text-gray-400">&middot; set from the type{{ kind === 'RANGE' ? ' and days' : '' }}</span>
            </div>
          </div>
        </section>
      </div>

      <!-- ============ SIDE: preview + usage ============ -->
      <div class="flex flex-col gap-5 lg:sticky lg:top-4">
        <section class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
            <mat-icon class="!text-[16px] !h-4 !w-4">visibility</mat-icon> Preview
          </div>
          <div class="p-5 flex flex-col gap-3">
            <div>
              <div class="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">Product page</div>
              <div class="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-3.5 py-3">
                <span class="h-9 w-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">{{ kind === 'CALL' ? 'call' : 'local_shipping' }}</mat-icon></span>
                <span class="text-sm font-semibold text-gray-900">{{ description || 'Your description' }}</span>
              </div>
            </div>
            <div>
              <div class="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">Checkout</div>
              <div class="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-3.5 py-3">
                <span class="h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0" [ngClass]="kind === 'OTHER' ? 'bg-amber-50 text-amber-600' : 'bg-neutral-900 text-amber-400'"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">shopping_cart_checkout</mat-icon></span>
                <span class="text-sm" [ngClass]="kind === 'OTHER' ? 'text-amber-700' : 'text-gray-800'">
                  @switch (kind) {
                    @case ('RANGE') { Estimated delivery is <strong>{{ from || '?' }} to {{ to || '?' }}</strong> working days }
                    @case ('CALL') { <strong>Call to arrange</strong> delivery }
                    @default { No delivery estimate }
                  }
                </span>
              </div>
            </div>
          </div>
        </section>

        @if (editing) {
        <section class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <span class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-400"><mat-icon class="!text-[16px] !h-4 !w-4">inventory_2</mat-icon> Variants on this term</span>
            <span class="text-xs font-semibold text-gray-500 tabular-nums">{{ editing.variants | number }}</span>
          </div>
          @if (variantsLoading) {
            <div class="py-8 flex justify-center"><mat-spinner diameter="22"></mat-spinner></div>
          } @else if (!variants.length) {
            <div class="px-5 py-8 text-center text-sm text-gray-400">No variants use this term yet</div>
          } @else {
            <div class="max-h-[340px] overflow-y-auto p-2">
              @for (v of variants; track v.productVariantId) {
              <a class="group flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-amber-50/70" [routerLink]="['/products/edit', v.productSlug]" target="_blank" rel="noopener">
                <div class="min-w-0 flex-1">
                  <div class="font-mono text-xs font-semibold text-gray-900 truncate">{{ v.sku }}</div>
                  <div class="text-[11px] text-gray-500 truncate">{{ v.productName }}</div>
                </div>
                <mat-icon class="!text-[15px] !h-[15px] !w-[15px] text-gray-300 group-hover:text-amber-600">open_in_new</mat-icon>
              </a>
              }
            </div>
          }
        </section>
        }
      </div>
    </div>

    <!-- Save bar -->
    <div class="sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 bg-white/95 backdrop-blur rounded-2xl border border-gray-200 shadow-lg px-5 py-3">
      <div class="min-w-0 text-sm">
        @if (formError) {
          <span class="flex items-center gap-1.5 text-red-600"><mat-icon class="!text-[16px] !h-4 !w-4 flex-shrink-0">error_outline</mat-icon><span class="truncate">{{ formError }}</span></span>
        } @else if (editing && !dirty) {
          <span class="text-gray-400">No changes yet</span>
        } @else {
          <span class="flex items-center gap-1.5 text-emerald-600"><mat-icon class="!text-[16px] !h-4 !w-4">check_circle</mat-icon>Ready to save</span>
        }
      </div>
      <div class="flex items-center gap-2 ml-auto">
        <a [routerLink]="['/delivery-terms']" [queryParams]="editing ? { selected: editing.id } : {}" class="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors">Cancel</a>
        <button type="button" [disabled]="!!formError || saving || (editing && !dirty)" (click)="save()"
          class="flex items-center gap-1.5 px-6 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 text-sm font-bold shadow-sm hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-40 disabled:pointer-events-none">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">{{ saving ? 'hourglass_empty' : 'save' }}</mat-icon>
          {{ saving ? 'Saving…' : editing ? 'Save changes' : 'Create term' }}
        </button>
      </div>
    </div>
    }
  </div>
  </div>
</app-main-layout>
